import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Table,
  Input,
  Tag,
  Tooltip,
  Badge,
  Space,
  message,
  Tabs,
  Popconfirm,
  Select,
  Progress,
} from "antd";
import { useSelector } from "react-redux";
import {
  getPulseReports,
  deletePulseReport,
  activatePulseReport,
  pausePulseReport,
  resumePulseReport,
  archivePulseReport,
  sendPulseReportNow,
  pauseRunningPulseReport,
  resumeFromCheckpoint,
  stopPulseReport,
  getPulseReportProgress,
} from "@api/pulse-reports/pulseReport";
import { useQuery, useQueryClient } from "react-query";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  MailOutlined,
  BarChartOutlined,
  TeamOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SendOutlined,
  PlusOutlined,
  SyncOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

const { Search } = Input;
const { TabPane } = Tabs;
const { Option } = Select;

// Report type labels and colors
const reportTypeConfig = {
  invoice: { label: "Invoice", color: "gold" },
  usage: { label: "Usage", color: "blue" },
  performance: { label: "Performance", color: "purple" },
  analytics: { label: "Analytics", color: "cyan" },
  digest: { label: "Digest", color: "green" },
  newsletter: { label: "Newsletter", color: "magenta" },
  custom: { label: "Custom", color: "default" },
};

// Status labels and colors
const statusConfig = {
  draft: { label: "Draft", color: "default", icon: <EditOutlined /> },
  active: { label: "Active", color: "success", icon: <PlayCircleOutlined /> },
  paused: { label: "Paused", color: "warning", icon: <PauseCircleOutlined /> },
  sending: { label: "Sending", color: "processing", icon: <SyncOutlined spin /> },
  archived: { label: "Archived", color: "default", icon: <FileTextOutlined /> },
  failed: { label: "Failed", color: "error", icon: <ExclamationCircleOutlined /> },
};

// Schedule type labels
const scheduleTypeLabels = {
  on_refill: "On Refill",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  custom_cron: "Custom Cron",
  manual: "Manual",
};

const PulseReportsList = ({ history }) => {
  const { user } = useSelector((state) => state.auth);
  const queryClient = useQueryClient();

  // Main state
  const [pulseReports, setPulseReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [reportTypeFilter, setReportTypeFilter] = useState(null);
  const [statusCounts, setStatusCounts] = useState({
    draft: 0,
    active: 0,
    paused: 0,
    sending: 0,
    archived: 0,
    failed: 0,
    total: 0,
  });
  const [sendingProgress, setSendingProgress] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [actionLoading, setActionLoading] = useState({});

  // Fetch pulse reports
  const fetchPulseReportsData = useCallback(async () => {
    try {
      const status = activeTab === "all" ? undefined : activeTab;
      const res = await getPulseReports(
        pagination.current,
        pagination.pageSize,
        status,
        reportTypeFilter,
        searchText
      );
      return res;
    } catch (err) {
      console.error("Fetch pulse reports error", err);
      throw err;
    }
  }, [pagination.current, pagination.pageSize, searchText, activeTab, reportTypeFilter]);

  const { data, isLoading, error } = useQuery(
    [
      "pulseReports",
      pagination.current,
      pagination.pageSize,
      searchText,
      activeTab,
      reportTypeFilter,
    ],
    fetchPulseReportsData,
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      refetchOnWindowFocus: true,
      onSuccess: (response) => {
        const reports = response.data?.data || [];
        setPulseReports(reports);
        setPagination((p) => ({
          ...p,
          total: response.data?.pagination?.total || 0,
        }));

        // Calculate status counts from data
        const counts = reports.reduce(
          (acc, report) => {
            acc[report.status] = (acc[report.status] || 0) + 1;
            acc.total += 1;
            return acc;
          },
          { draft: 0, active: 0, paused: 0, sending: 0, archived: 0, failed: 0, total: 0 }
        );
        setStatusCounts(counts);

        // Fetch progress for any sending reports
        const sendingReports = reports.filter((r) => r.status === "sending");
        if (sendingReports.length > 0) {
          sendingReports.forEach(async (report) => {
            try {
              const progressRes = await getPulseReportProgress(report._id);
              if (progressRes.data) {
                setSendingProgress((prev) => ({
                  ...prev,
                  [report._id]: progressRes.data,
                }));
              }
            } catch (err) {
              console.error("Error fetching progress for", report._id, err);
            }
          });
        }
      },
    }
  );

  // Effect to update loading state
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Handle pagination
  const handlePaginationChange = (page, pageSize) => {
    setPagination((prev) => ({ ...prev, current: page, pageSize }));
  };

  // Handle search
  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Navigate to create
  const handleCreate = () => {
    history.push("/super-admin/pulse-reports/create");
  };

  // Navigate to edit
  const handleEdit = (id) => {
    history.push(`/super-admin/pulse-reports/${id}/edit`);
  };

  // Navigate to stats/detail
  const handleViewStats = (id) => {
    history.push(`/super-admin/pulse-reports/${id}/stats`);
  };

  // Navigate to recipients
  const handleViewRecipients = (id) => {
    history.push(`/super-admin/pulse-reports/${id}/recipients`);
  };

  // Delete pulse report
  const handleDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this pulse report?",
      content: "This action cannot be undone. All associated data will be deleted.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deletePulseReport(id);
          message.success("Pulse report deleted successfully");
          queryClient.invalidateQueries("pulseReports");
        } catch (error) {
          console.error("Error deleting pulse report", error);
          message.error("Failed to delete pulse report");
        }
      },
    });
  };

  // Activate pulse report
  const handleActivate = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "activate" }));
    try {
      await activatePulseReport(id);
      message.success("Pulse report activated successfully");
      queryClient.invalidateQueries("pulseReports");
    } catch (error) {
      console.error("Error activating pulse report", error);
      message.error("Failed to activate pulse report");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  // Pause pulse report
  const handlePause = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "pause" }));
    try {
      await pausePulseReport(id);
      message.success("Pulse report paused successfully");
      queryClient.invalidateQueries("pulseReports");
    } catch (error) {
      console.error("Error pausing pulse report", error);
      message.error("Failed to pause pulse report");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  // Resume pulse report
  const handleResume = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "resume" }));
    try {
      await resumePulseReport(id);
      message.success("Pulse report resumed successfully");
      queryClient.invalidateQueries("pulseReports");
    } catch (error) {
      console.error("Error resuming pulse report", error);
      message.error("Failed to resume pulse report");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  // Archive pulse report
  const handleArchive = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "archive" }));
    try {
      await archivePulseReport(id);
      message.success("Pulse report archived successfully");
      queryClient.invalidateQueries("pulseReports");
    } catch (error) {
      console.error("Error archiving pulse report", error);
      message.error("Failed to archive pulse report");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  // Send now
  const handleSendNow = async (id) => {
    Modal.confirm({
      title: "Send Pulse Report Now?",
      content: "This will immediately send the report to all targeted users.",
      okText: "Send Now",
      cancelText: "Cancel",
      onOk: async () => {
        setActionLoading((prev) => ({ ...prev, [id]: "send" }));
        try {
          const result = await sendPulseReportNow(id);
          message.success(
            `Pulse report sending started! ${result.data?.sent || 0} users targeted.`
          );
          queryClient.invalidateQueries("pulseReports");
        } catch (error) {
          console.error("Error sending pulse report", error);
          message.error("Failed to send pulse report");
        } finally {
          setActionLoading((prev) => ({ ...prev, [id]: null }));
        }
      },
    });
  };

  // Pause running pulse report (mid-send)
  const handlePauseRunning = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "pauseRunning" }));
    try {
      const result = await pauseRunningPulseReport(id);
      message.success("Pulse report paused. You can resume from checkpoint later.");
      queryClient.invalidateQueries("pulseReports");
    } catch (error) {
      console.error("Error pausing running pulse report", error);
      message.error("Failed to pause pulse report");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  // Resume from checkpoint
  const handleResumeFromCheckpoint = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "resumeCheckpoint" }));
    try {
      const result = await resumeFromCheckpoint(id);
      message.success("Pulse report resumed from checkpoint");
      queryClient.invalidateQueries("pulseReports");
    } catch (error) {
      console.error("Error resuming pulse report", error);
      message.error("Failed to resume pulse report");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  // Stop pulse report
  const handleStop = async (id) => {
    Modal.confirm({
      title: "Stop Pulse Report?",
      content: "This will permanently stop the pulse report. You cannot resume after stopping.",
      okText: "Stop",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setActionLoading((prev) => ({ ...prev, [id]: "stop" }));
        try {
          const result = await stopPulseReport(id);
          message.success(
            `Pulse report stopped. Sent: ${result.data?.finalStats?.sent || 0}, Failed: ${result.data?.finalStats?.failed || 0}`
          );
          queryClient.invalidateQueries("pulseReports");
        } catch (error) {
          console.error("Error stopping pulse report", error);
          message.error("Failed to stop pulse report");
        } finally {
          setActionLoading((prev) => ({ ...prev, [id]: null }));
        }
      },
    });
  };

  // Retry failed pulse report
  const handleRetry = async (id) => {
    Modal.confirm({
      title: "Retry Failed Pulse Report?",
      content: "This will attempt to resume the pulse report from where it failed.",
      okText: "Retry",
      cancelText: "Cancel",
      onOk: async () => {
        setActionLoading((prev) => ({ ...prev, [id]: "retry" }));
        try {
          const result = await resumeFromCheckpoint(id);
          message.success("Pulse report retry started");
          queryClient.invalidateQueries("pulseReports");
        } catch (error) {
          console.error("Error retrying pulse report", error);
          message.error("Failed to retry pulse report");
        } finally {
          setActionLoading((prev) => ({ ...prev, [id]: null }));
        }
      },
    });
  };

  // Table columns
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Tooltip title={name}>
            <span style={{ fontWeight: 500 }}>
              {name?.length > 30 ? `${name.substring(0, 30)}...` : name}
            </span>
          </Tooltip>
          {record.description && (
            <Tooltip title={record.description}>
              <span style={{ fontSize: "12px", color: "#888" }}>
                {record.description?.length > 40
                  ? `${record.description.substring(0, 40)}...`
                  : record.description}
              </span>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "reportType",
      key: "reportType",
      width: 120,
      render: (type) => {
        const config = reportTypeConfig[type] || { label: type, color: "default" };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: "Schedule",
      dataIndex: "schedule",
      key: "schedule",
      width: 120,
      render: (schedule) => {
        if (!schedule) return "-";
        return (
          <Tooltip title={`${scheduleTypeLabels[schedule.type]} at ${schedule.timeOfDay?.hour || 9}:00`}>
            <Tag icon={<ClockCircleOutlined />}>
              {scheduleTypeLabels[schedule.type]}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status, record) => {
        const config = statusConfig[status] || { label: status, color: "default" };

        // Show progress bar for sending status
        if (status === "sending") {
          const progress = sendingProgress[record._id];
          const percentage = progress?.progress?.percentage || 0;
          return (
            <Space direction="vertical" size={2} style={{ width: "100%" }}>
              <Tag color={config.color} icon={config.icon}>
                {config.label.toUpperCase()}
              </Tag>
              <Progress
                percent={percentage}
                size="small"
                status="active"
                strokeColor="#1890ff"
              />
              {progress && (
                <span style={{ fontSize: "11px", color: "#666" }}>
                  {progress.progress?.sent || 0}/{progress.progress?.total || 0}
                </span>
              )}
            </Space>
          );
        }

        // Show checkpoint info for paused reports that have one
        if (status === "paused" && record.statistics?.lastCheckpoint?.index > 0) {
          return (
            <Space direction="vertical" size={2}>
              <Tag color={config.color} icon={config.icon}>
                {config.label.toUpperCase()}
              </Tag>
              <span style={{ fontSize: "11px", color: "#faad14" }}>
                Checkpoint: {record.statistics.lastCheckpoint.processedCount || 0} sent
              </span>
            </Space>
          );
        }

        // Show failure reason for failed status
        if (status === "failed") {
          return (
            <Space direction="vertical" size={2}>
              <Tag color={config.color} icon={config.icon}>
                {config.label.toUpperCase()}
              </Tag>
              {record.statistics?.pauseReason && (
                <Tooltip title={record.statistics.pauseReason}>
                  <span style={{ fontSize: "11px", color: "#ff4d4f" }}>
                    {record.statistics.pauseReason.substring(0, 20)}...
                  </span>
                </Tooltip>
              )}
            </Space>
          );
        }

        return (
          <Tag color={config.color} icon={config.icon}>
            {config.label.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Stats",
      key: "stats",
      width: 180,
      render: (_, record) => {
        const stats = record.statistics || {};
        return (
          <Space direction="vertical" size={0}>
            <span>
              <MailOutlined /> Sent: {stats.totalSent || 0}
            </span>
            <span style={{ color: "#52c41a" }}>
              <EyeOutlined /> Opens: {stats.openRate || 0}%
            </span>
            <span style={{ color: "#1890ff" }}>
              Clicks: {stats.clickRate || 0}%
            </span>
          </Space>
        );
      },
    },
    {
      title: "Next Run",
      key: "nextRun",
      width: 140,
      render: (_, record) => {
        const nextRun = record.statistics?.nextRunAt;
        if (!nextRun) return <span style={{ color: "#888" }}>-</span>;
        return (
          <Tooltip title={new Date(nextRun).toLocaleString()}>
            <span>{new Date(nextRun).toLocaleDateString()}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 320,
      render: (_, record) => (
        <Space wrap>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record._id)}
            disabled={record.status === "active" || record.status === "sending"}
          >
            Edit
          </Button>

          <Button
            size="small"
            icon={<BarChartOutlined />}
            onClick={() => handleViewStats(record._id)}
          >
            Stats
          </Button>

          <Button
            size="small"
            icon={<TeamOutlined />}
            onClick={() => handleViewRecipients(record._id)}
          >
            Recipients
          </Button>

          {record.status === "draft" && (
            <Button
              type="primary"
              size="small"
              ghost
              icon={<PlayCircleOutlined />}
              loading={actionLoading[record._id] === "activate"}
              onClick={() => handleActivate(record._id)}
            >
              Activate
            </Button>
          )}

          {record.status === "active" && (
            <>
              <Button
                size="small"
                icon={<PauseCircleOutlined />}
                loading={actionLoading[record._id] === "pause"}
                onClick={() => handlePause(record._id)}
              >
                Pause
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<SendOutlined />}
                loading={actionLoading[record._id] === "send"}
                onClick={() => handleSendNow(record._id)}
              >
                Send Now
              </Button>
            </>
          )}

          {record.status === "sending" && (
            <>
              <Button
                size="small"
                icon={<PauseCircleOutlined />}
                loading={actionLoading[record._id] === "pauseRunning"}
                onClick={() => handlePauseRunning(record._id)}
              >
                Pause
              </Button>
              <Button
                danger
                size="small"
                icon={<StopOutlined />}
                loading={actionLoading[record._id] === "stop"}
                onClick={() => handleStop(record._id)}
              >
                Stop
              </Button>
            </>
          )}

          {record.status === "paused" && (
            <>
              {record.statistics?.lastCheckpoint?.index > 0 ? (
                <Button
                  type="primary"
                  size="small"
                  ghost
                  icon={<PlayCircleOutlined />}
                  loading={actionLoading[record._id] === "resumeCheckpoint"}
                  onClick={() => handleResumeFromCheckpoint(record._id)}
                >
                  Resume from Checkpoint
                </Button>
              ) : (
                <Button
                  type="primary"
                  size="small"
                  ghost
                  icon={<PlayCircleOutlined />}
                  loading={actionLoading[record._id] === "resume"}
                  onClick={() => handleResume(record._id)}
                >
                  Resume
                </Button>
              )}
              <Popconfirm
                title="Archive this pulse report?"
                onConfirm={() => handleArchive(record._id)}
                okText="Archive"
                cancelText="Cancel"
              >
                <Button
                  size="small"
                  loading={actionLoading[record._id] === "archive"}
                >
                  Archive
                </Button>
              </Popconfirm>
            </>
          )}

          {record.status === "failed" && (
            <>
              <Button
                type="primary"
                size="small"
                ghost
                icon={<ReloadOutlined />}
                loading={actionLoading[record._id] === "retry"}
                onClick={() => handleRetry(record._id)}
              >
                Retry
              </Button>
              <Popconfirm
                title="Archive this pulse report?"
                onConfirm={() => handleArchive(record._id)}
                okText="Archive"
                cancelText="Cancel"
              >
                <Button
                  size="small"
                  loading={actionLoading[record._id] === "archive"}
                >
                  Archive
                </Button>
              </Popconfirm>
            </>
          )}

          {(record.status === "draft" || record.status === "archived" || record.status === "failed") && (
            <Popconfirm
              title="Delete this pulse report?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record._id)}
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card style={{ borderRadius: "0", minHeight: "100vh" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <h3>Pulse Reports</h3>
          <p style={{ color: "#666", marginBottom: 0 }}>
            Manage automated reports sent to users - invoices, metrics, newsletters
          </p>
        </Col>
        <Col>
          <Space size="middle">
            <Button
              type="ghost"
              onClick={() => history.push("/super-admin/pulse-reports")}
            >
              Analytics Dashboard
            </Button>
            <Button
              type="ghost"
              onClick={() => history.push("/super-admin/pulse-reports/html-templates")}
            >
              HTML Templates
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Create Campaign
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Space size="middle">
              <Select
                placeholder="Filter by type"
                style={{ width: 180 }}
                allowClear
                value={reportTypeFilter}
                onChange={(value) => {
                  setReportTypeFilter(value);
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
              >
                {Object.entries(reportTypeConfig).map(([key, config]) => (
                  <Option key={key} value={key}>
                    {config.label}
                  </Option>
                ))}
              </Select>
              <Search
                placeholder="Search by name/description"
                style={{ width: 300 }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                allowClear
              />
            </Space>
          }
        >
          <TabPane
            tab={
              <span>
                All Reports
                <Badge count={statusCounts.total} style={{ marginLeft: 8 }} />
              </span>
            }
            key="all"
          />
          <TabPane
            tab={
              <span>
                Draft
                <Badge count={statusCounts.draft} style={{ marginLeft: 8 }} />
              </span>
            }
            key="draft"
          />
          <TabPane
            tab={
              <span>
                Active
                <Badge
                  count={statusCounts.active}
                  style={{ marginLeft: 8, backgroundColor: "#52c41a" }}
                />
              </span>
            }
            key="active"
          />
          <TabPane
            tab={
              <span>
                Sending
                <Badge
                  count={statusCounts.sending}
                  style={{ marginLeft: 8, backgroundColor: "#1890ff" }}
                />
              </span>
            }
            key="sending"
          />
          <TabPane
            tab={
              <span>
                Paused
                <Badge
                  count={statusCounts.paused}
                  style={{ marginLeft: 8, backgroundColor: "#faad14" }}
                />
              </span>
            }
            key="paused"
          />
          <TabPane
            tab={
              <span>
                Failed
                <Badge
                  count={statusCounts.failed}
                  style={{ marginLeft: 8, backgroundColor: "#ff4d4f" }}
                />
              </span>
            }
            key="failed"
          />
          <TabPane
            tab={
              <span>
                Archived
                <Badge count={statusCounts.archived} style={{ marginLeft: 8 }} />
              </span>
            }
            key="archived"
          />
        </Tabs>

        <Table
          columns={columns}
          dataSource={pulseReports}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} pulse reports`,
          }}
          loading={loading}
          onChange={({ current, pageSize }) =>
            handlePaginationChange(current, pageSize)
          }
          rowKey={(record) => record._id}
          scroll={{ x: 1200 }}
        />
      </Card>
    </Card>
  );
};

export default PulseReportsList;
